import { insertJudgeFileLinks } from "@/feedback/media_paths";
import { html, TemplateResult, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { computePythiaDiff, stripOuterHtml } from "@/feedback/pythia";
import { I18n } from "@/i18n/i18n";
import { renderMessage, renderMessages } from "@/feedback/message_helpers";
import { escapeHtml } from "@/utilities";
import { sanitizeHtml } from "@/feedback/sanitizer";
import type { DiffStyle, FeedbackEnvelopeContext, FileEntry, Test } from "@/types/FeedbackResult";

/**
 * Renders one test (an expected vs generated comparison) within a testcase.
 * Handles three rendering branches:
 *   - Pythia: pre-baked diff HTML from `test.data.diff` (sanitized before insertion).
 *   - Accepted CSV: rendered as a single styled table via `renderAcceptedCsv` (no diff needed when accepted).
 *   - All other cases: delegates to `d-feedback-diff` for text or CSV diff rendering.
 *
 * @element d-feedback-test
 *
 * @prop {Test}                     test              - Test data (generated, expected, format, messages, …).
 * @prop {string}                  diff-style        - Passed to `d-feedback-diff`; "split" or "unified".
 * @prop {FeedbackEnvelopeContext} files - Forwarded to message helpers for media-path rewriting.
 */
@customElement("d-feedback-test")
export class FeedbackTest extends DodonaElement {
    @property({ type: Object }) test!: Test;
    @property({ type: String }) diffStyle: DiffStyle = "split";
    @property({ type: Object }) submissionContext: FeedbackEnvelopeContext = {};
    @property({ type: Object }) files: Record<string, FileEntry> | null = null;

    @state() private _acceptedCsvHtml: string | null = null;
    /** Memoized pythia diff HTML; null when this test is not a pythia diff. */
    @state() private _pythiaHtml: string | null = null;

    override willUpdate(changedProperties: PropertyValues): void {
        super.willUpdate(changedProperties);

        if (changedProperties.has("test")) {
            this._updateAcceptedCsv();
        }

        // The pythia HTML additionally depends on the active diff style, the file
        // set, and the submission context (activity path / access token).
        if (
            changedProperties.has("test") ||
            changedProperties.has("diffStyle") ||
            changedProperties.has("files") ||
            changedProperties.has("submissionContext")
        ) {
            this._updatePythiaHtml();
        }
    }

    private _updateAcceptedCsv(): void {
        const t = this.test;
        this._acceptedCsvHtml = null;
        if (!t || !t.accepted || t.format !== "csv" || t.data?.diff) {
            return;
        }
        void import("@/feedback/diff/csv").then((mod) => {
            if (this.test !== t) return;
            try {
                this._acceptedCsvHtml = mod.renderAcceptedCsv(t.generated ?? "") ?? "";
            } catch {
                this._acceptedCsvHtml = "";
            }
        });
    }

    private _updatePythiaHtml(): void {
        const t = this.test;
        if (!t?.data?.diff) {
            this._pythiaHtml = null;
            return;
        }
        if (t.accepted) {
            const htmlParts = t.data.diff
                .map((l) => {
                    if (l[2] !== null) return stripOuterHtml(l[2]);
                    if (l[3] !== null) return stripOuterHtml(l[3]);
                    return "";
                })
                .join("\n");
            this._pythiaHtml = this._linkifyFiles(sanitizeHtml(htmlParts));
        } else {
            const tables = computePythiaDiff(t.data.diff);
            const activeHtml = this.diffStyle === "split" ? tables.split : tables.unified;
            this._pythiaHtml = this._linkifyFiles(activeHtml);
        }
    }

    render(): TemplateResult {
        const t = this.test;
        if (!t) return html``;

        // Determine effective channel (from test.channel or test.data.channel)
        const channel = t.channel ?? t.data?.channel ?? null;

        // Description or channel badge
        let descriptionSlot: TemplateResult;
        if (t.description) {
            descriptionSlot = html`<div class="description">
                ${renderMessage(t.description, this.submissionContext, this.files)}
            </div>`;
        } else if (channel) {
            const badgeClass = t.accepted ? "bg-success" : "bg-danger";
            descriptionSlot = html`<div class="description">
                <span class="badge ${badgeClass}">${escapeHtml(channel)}</span>
            </div>`;
        } else {
            descriptionSlot = html``;
        }

        // Accepted or failed content
        let diffSlot: TemplateResult;
        if (t.accepted) {
            diffSlot = html`<div class="test-accepted">${this._renderAccepted(t)}</div>`;
        } else {
            diffSlot = html`<div class="test-failed">${this._renderFailed(t)}</div>`;
        }

        return html`
            <div class="test">
                ${descriptionSlot} ${diffSlot}
                ${t.messages?.length ? renderMessages(t.messages, this.submissionContext, this.files) : ""}
            </div>
        `;
    }

    private _renderAccepted(t: Test): TemplateResult {
        // Pythia diff: accepted with pre-baked diff data (linkified in willUpdate).
        if (t.data?.diff) {
            return html`<span class="output">${unsafeHTML(this._pythiaHtml ?? "")}</span>`;
        }

        // CSV accepted (renderAcceptedCsv is dynamic-imported; falls through to
        // plain text while the module loads).
        if (t.format === "csv" && this._acceptedCsvHtml) {
            return html`${unsafeHTML(this._acceptedCsvHtml)}`;
        }

        // Text accepted: render the generated output, truncated for very long values
        let generated = t.generated ?? "";
        let truncated = false;
        if (generated.length > 5000) {
            truncated = true;
            generated = generated.slice(0, 5000) + "...";
        }
        return html`
            <span class="output">${generated}</span>
            ${
                truncated
                    ? html`<div class="callout callout-warning">
                          ${I18n.t("js.submissions_show.cut_off_message_generated")}
                      </div>`
                    : ""
            }
        `;
    }

    private _renderFailed(t: Test): TemplateResult {
        // Pythia diff: use pre-baked diff data (computed + linkified in willUpdate).
        if (t.data?.diff) {
            return html`<div class="diffs">${unsafeHTML(this._pythiaHtml ?? "")}</div>`;
        }

        // CSV or text diff: delegate to d-feedback-diff
        return html`<d-feedback-diff
            .generated=${t.generated ?? ""}
            .expected=${t.expected ?? ""}
            .format=${t.format ?? "text"}
            .diffStyle=${this.diffStyle}
        ></d-feedback-diff>`;
    }

    private _linkifyFiles(htmlString: string): string {
        return insertJudgeFileLinks(
            htmlString,
            this.files,
            this.submissionContext.activityPath,
            this.submissionContext.accessToken,
        );
    }
}
